import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import MarketingShell from "./MarketingShell";
import { Plus } from "@phosphor-icons/react";
import { formatLocal, formatLocalDate } from "@/lib/timezone";

const STATUS_COLORS = {
  completed: { bg: "#F0FDF4", fg: "#16A34A" },
  failed: { bg: "#FEF2F2", fg: "#DC2626" },
  mapping: { bg: "#EFF6FF", fg: "#0055FF" },
  validated: { bg: "#EFF6FF", fg: "#0055FF" },
  importing: { bg: "#FEF3C7", fg: "#92400E" },
  uploading: { bg: "#F5F5F5", fg: "#525252" },
};

export default function ImportsListPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { setRows((await api.get("/marketing/imports")).data); }
      catch { toast.error("Failed to load imports"); }
      setLoading(false);
    })();
  }, []);

  return (
    <MarketingShell>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-[#525252]">History of CSV imports. Each batch retains its provenance and consent metadata.</p>
        <button onClick={() => nav("/marketing/imports/new")}
                className="h-9 px-3 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1"
                data-testid="new-import-btn">
          <Plus size={12} weight="bold" />New Import
        </button>
      </div>

      <div className="bg-white border border-[#E5E5E5]">
        {loading ? <p className="p-8 text-center text-sm text-[#A3A3A3]">Loading…</p>
        : rows.length === 0 ? <p className="p-8 text-center text-sm text-[#A3A3A3]">No imports yet.</p>
        : (
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#A3A3A3]">
              <tr>
                <th className="text-left px-5 py-2 font-bold">Name</th>
                <th className="text-left px-3 py-2 font-bold">Vendor</th>
                <th className="text-left px-3 py-2 font-bold">Status</th>
                <th className="text-left px-3 py-2 font-bold">Consent</th>
                <th className="text-right px-3 py-2 font-bold">Rows</th>
                <th className="text-right px-3 py-2 font-bold">Imported</th>
                <th className="text-right px-3 py-2 font-bold">Skipped</th>
                <th className="text-right px-5 py-2 font-bold">Uploaded</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const c = STATUS_COLORS[r.status] || STATUS_COLORS.uploading;
                return (
                  <tr key={r.id} onClick={() => nav(`/marketing/imports/${r.id}`)}
                      className="border-t border-[#F5F5F5] hover:bg-[#FAFAFA] cursor-pointer" data-testid={`import-row-${r.id}`}>
                    <td className="px-5 py-3 font-semibold text-[#0A0A0A]">{r.name}</td>
                    <td className="px-3 py-3 text-xs text-[#525252]">{r.vendor || "—"}</td>
                    <td className="px-3 py-3">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5" style={{ backgroundColor: c.bg, color: c.fg }}>{r.status}</span>
                    </td>
                    <td className="px-3 py-3 text-xs text-[#525252]">{r.consent_declaration}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{r.row_count}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-[#16A34A] font-semibold">{r.imported_count}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-[#DC2626]">{r.skipped_count + r.error_count}</td>
                    <td className="px-5 py-3 text-right text-xs text-[#A3A3A3]">{formatLocal(r.uploaded_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </MarketingShell>
  );
}
